// Simple ContrastAnalysisService implementation

const ContrastAnalysisService = {
  /**
   * Analyzes a region of an image to determine optimal contrast
   *
   * @param {HTMLImageElement} image - Image element to analyze
   * @param {Object} options - Configuration options
   * @param {Object} [options.region] - Region to analyze {x, y, width, height}
   * @param {number} [options.samplingRate=5] - Sample every nth pixel (higher = faster)
   * @param {number} [options.brightnessThreshold=128] - Threshold to determine light/dark (0-255)
   * @param {boolean} [options.ignoreTransparency=true] - Whether to skip transparent pixels
   * @returns {Object} - Analysis results
   */
  analyzeRegion(image, options = {}) {
    // Default options
    const {
      region = null,
      samplingRate = 5,
      brightnessThreshold = 128,
      ignoreTransparency = true,
    } = options;

    // Default result in case of error
    const defaultResult = {
      contrast: "light",
      brightness: 0,
      isDark: true,
      isValid: false,
    };

    // Validate image
    if (!image || !image.complete || image.naturalWidth === 0) {
      return defaultResult;
    }

    try {
      // Create canvas for analysis
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Determine region dimensions
      let x = 0;
      let y = 0;
      let width = image.naturalWidth;
      let height = image.naturalHeight;

      // If a specific region is provided, use it
      if (region) {
        x = Math.min(Math.max(0, region.x), image.naturalWidth - 1);
        y = Math.min(Math.max(0, region.y), image.naturalHeight - 1);
        width = Math.min(region.width, image.naturalWidth - x);
        height = Math.min(region.height, image.naturalHeight - y);
      }

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Draw image region to canvas
      ctx.drawImage(image, x, y, width, height, 0, 0, width, height);

      // Get pixel data
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // Analyze pixel data
      let totalBrightness = 0;
      let pixelCount = 0;
      const step = samplingRate * 4; // 4 values per pixel (RGBA)

      for (let i = 0; i < data.length; i += step) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Skip transparent pixels if configured to do so
        if (ignoreTransparency && a < 128) continue;

        // Calculate perceived brightness using the luminance formula
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
        totalBrightness += brightness;
        pixelCount++;
      }

      // If no pixels were analyzed
      if (pixelCount === 0) {
        return defaultResult;
      }

      // Calculate average brightness
      const averageBrightness = totalBrightness / pixelCount;
      const isDark = averageBrightness < brightnessThreshold;

      // Return analysis result
      return {
        contrast: isDark ? "light" : "dark", // What text/icon color to use
        brightness: averageBrightness, // Average brightness value (0-255)
        isDark, // Whether region is dark
        isValid: true, // Analysis succeeded
      };
    } catch (error) {
      return defaultResult;
    }
  },

  /**
   * Convenience method for UI elements in an image viewer
   *
   * @param {HTMLImageElement} image - Image element
   * @param {Object} options - Analysis options
   * @returns {Object} - Analysis results for common UI regions
   */
  analyzeImageViewer(image, options = {}) {
    if (!image || !image.complete) {
      return {
        topRight: { contrast: "light", isValid: false },
        bottomRight: { contrast: "light", isValid: false },
      };
    }

    const imgWidth = image.naturalWidth;
    const imgHeight = image.naturalHeight;

    // Define standard regions for image viewer UI
    const topRightRegion = {
      x: Math.max(0, imgWidth - 60),
      y: 0,
      width: 60,
      height: 60,
    };

    const bottomRightRegion = {
      x: Math.max(0, imgWidth - 60),
      y: Math.max(0, imgHeight - 180),
      width: 60,
      height: 180,
    };

    return {
      topRight: this.analyzeRegion(image, {
        ...options,
        region: topRightRegion,
      }),
      bottomRight: this.analyzeRegion(image, {
        ...options,
        region: bottomRightRegion,
      }),
    };
  },
};

export default ContrastAnalysisService;
